/**
 * Rekenwerk voor de labelfeed (MA-61), los van de componenten zodat het te
 * testen is: waar een onbekend adres naartoe linkt, hoe oud een registratie is
 * en hoe een aandeel op het scherm komt.
 */
import type { LabelfeedRij } from '@mamimap/shared';
import { ROUTES } from '@/lib/router';

/** Een adres dat nog niet in onze vangst staat: zoeken op het web, Funda voorop. */
export function zoeklink(adres: string): string {
  return `https://www.google.com/search?q=${encodeURIComponent(`${adres} Amsterdam funda`)}`;
}

/** De woning uit de vangst openen op de kaart, met het popupje open (MA-59). */
export function woningUrl(id: string): string {
  return `${ROUTES.kaart}?woning=${id}`;
}

/** Hoeveel hele dagen een dag (`2026-10-14`) geleden is; nul is vandaag. */
export function dagenGeleden(dag: string, vandaag: Date = new Date()): number {
  const van = Date.UTC(
    Number(dag.slice(0, 4)),
    Number(dag.slice(5, 7)) - 1,
    Number(dag.slice(8, 10)),
  );
  const tot = Date.UTC(vandaag.getUTCFullYear(), vandaag.getUTCMonth(), vandaag.getUTCDate());
  return Math.max(0, Math.round((tot - van) / 86_400_000));
}

/** `0.44` -> `44%`; null wordt een streepje. */
export function formatAandeel(aandeel: number | null): string {
  return aandeel === null ? '–' : `${Math.round(aandeel * 100)}%`;
}

/** Wat er bij de woning staat: te koop sinds, of nog niet gezien. */
export function woningTekst(
  rij: Pick<LabelfeedRij, 'woning'>,
  formatDatum: (dag: string) => string,
): string {
  const woning = rij.woning;
  if (!woning) return 'nog niet gezien';
  if (woning.status === 'verdwenen') return 'verdwenen van Funda';
  return woning.teKoopSinds ? `te koop sinds ${formatDatum(woning.teKoopSinds)}` : 'te koop';
}

/** Een keuze in de buurt- of wijklijst: code, naam en hoeveel rijen erin vallen. */
export type Gebiedskeuze = { code: string; naam: string; aantal: number };

/** De wijken en buurten die in de rijen voorkomen, op naam, met hun aantal. */
export function gebiedskeuzes(rijen: readonly LabelfeedRij[]): {
  wijken: Gebiedskeuze[];
  buurten: (Gebiedskeuze & { wijkcode: string | null })[];
} {
  const wijken = new Map<string, Gebiedskeuze>();
  const buurten = new Map<string, Gebiedskeuze & { wijkcode: string | null }>();
  for (const rij of rijen) {
    if (rij.wijkcode && rij.wijknaam) {
      const wijk = wijken.get(rij.wijkcode) ?? {
        code: rij.wijkcode,
        naam: rij.wijknaam,
        aantal: 0,
      };
      wijk.aantal += 1;
      wijken.set(rij.wijkcode, wijk);
    }
    if (rij.buurtcode && rij.buurtnaam) {
      const buurt = buurten.get(rij.buurtcode) ?? {
        code: rij.buurtcode,
        naam: rij.buurtnaam,
        wijkcode: rij.wijkcode,
        aantal: 0,
      };
      buurt.aantal += 1;
      buurten.set(rij.buurtcode, buurt);
    }
  }
  const opNaam = (a: Gebiedskeuze, b: Gebiedskeuze) => a.naam.localeCompare(b.naam, 'nl');
  return { wijken: [...wijken.values()].sort(opNaam), buurten: [...buurten.values()].sort(opNaam) };
}

/**
 * Alleen de rijen in de gekozen wijken en buurten. Niets gekozen is geen
 * filter. Kies je een wijk én een buurt in een andere wijk, dan tellen ze
 * allebei: het is "deze plekken", geen doorsnede.
 */
export function filterOpGebied(
  rijen: readonly LabelfeedRij[],
  keuze: { wijken: readonly string[]; buurten: readonly string[] },
): LabelfeedRij[] {
  if (keuze.wijken.length === 0 && keuze.buurten.length === 0) return [...rijen];
  const wijken = new Set(keuze.wijken);
  const buurten = new Set(keuze.buurten);
  return rijen.filter(
    (rij) =>
      (rij.wijkcode !== null && wijken.has(rij.wijkcode)) ||
      (rij.buurtcode !== null && buurten.has(rij.buurtcode)),
  );
}
